import { computed, toValue } from "vue";
import type {
  AuthorizationRuleGroupForGating,
  UnnormalizedAuthorizationRuleGroupForGatingType,
} from "../use-gating-permissions";
import { generateMaskForLevel } from "@f3/shared/permissions";
import type { GatingMaskInformation } from "../gating-types";
import { useGatingEvaluator } from "./use-gating-evaluator";

export function useGatingRequestedPermissions(
  unnormalizedGroup: UnnormalizedAuthorizationRuleGroupForGatingType,
) {
  const authorizationRuleGroup = computed(
    () =>
      (toValue(unnormalizedGroup) ??
        null) as AuthorizationRuleGroupForGating | null,
  );

  const { getGatingMaskInformation } = useGatingEvaluator();

  const requestedPermissions = computed(() =>
    getRequestedPermissionForGroup(authorizationRuleGroup.value, {
      getGatingMaskInformation,
    }),
  );

  return {
    authorizationRuleGroup,
    requestedPermissions,
  };
}

export function getRequestedPermissionForGroup(
  group: AuthorizationRuleGroupForGating | null,
  evaluator: {
    getGatingMaskInformation: (
      rule: AuthorizationRuleGroupForGating["authorization_rules"][number],
    ) => GatingMaskInformation | null;
  },
): GatingMaskInformation {
  let resultMask = 0;
  const resultLevel = 0;

  if (!group)
    return { permission_mask: resultMask, permission_mask_level: resultLevel };

  const masks: GatingMaskInformation[] = [];

  for (const rule of group.authorization_rules) {
    const mask = evaluator.getGatingMaskInformation(rule);
    if (!mask) continue;
    masks.push(mask);
  }

  for (const mask of masks) {
    const permissionMask = mask.permission_mask;
    const permissionMaskLevel = mask.permission_mask_level;

    if (resultLevel >= permissionMaskLevel) {
      resultMask |= generateMaskForLevel(permissionMask, permissionMaskLevel);
    }
  }

  return {
    permission_mask: resultMask,
    permission_mask_level: resultLevel,
  };
}
